import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { SetPuzzleID } from '../../store/puzzle-id.actions';
import { PuzzleGameTypesEnum } from '../../puzzle-framework-module/puzzle-game-types.enum';
import { IncMoveCounter, SetFinished, SetGameProgress } from '../../store/puzzle-game.actions';
import { Button } from '../../shared-module/buttons/button.def';
import {
  PUZZLE_WIN_DIALOG_BUTTONS,
  PuzzleWinDialogService,
} from '../../shared-module/puzzle-win-dialog/puzzle-win-dialog.service';
import { PuzzleHelpDialogService } from '../../shared-module/puzzle-help-dialog/puzzle-help-dialog.service';
import { PuzzleSettingsModalService } from '../../shared-module/puzzle-settings-modal/puzzle-settings-modal.service';
import { BridgesHelpComponent } from '../help/bridges-help.component';
import { BridgesWinScreenComponent } from '../win-screen/bridges-win-screen.component';
import { BridgesIsland } from '../interfaces/bridges-island';
import { BridgeKey } from '../interfaces/bridge-key';
import { BridgesPuzzleConfig } from '../interfaces/bridges-puzzle-config';
import { parseBridgesPuzzleConfig } from '../config/bridges-puzzle-config.adapter';
import {
  BridgesMap,
  LineEndpoints,
  computeNeighborPairs,
  computeProgress,
  createsCrossing,
  currentDegree,
  findCheckErrors,
  findClosedStrandedIslands,
  getOrientation,
  islandBounds,
  isSolved,
  isStarved,
  matchesSolution,
  raycast,
  isVisiblePair,
  lineEndpointsH,
  lineEndpointsV,
  normalizeKey,
  serializeKey,
} from '../geometry/bridges-geometry';
import { BridgesPointerController, BridgesPointerIntent } from '../interaction/bridges-pointer-controller';
import { BridgesSizing, computeBridgesSizing } from '../sizing/bridges-responsive-sizing';
import { InitBridgesGame, PersistBridgesState } from '../store/bridges-game.actions';
import { BridgesGameModel } from '../store/bridges-game.model';
import { BridgesGameState } from '../store/bridges-game.state';

/**
 * One drawn line of a bridge slot.
 *
 * Every rail is drawn on the slot's centred span and pushed to its side with a
 * transform, rather than being given its own endpoints. Together with the
 * stable `id` this lets Angular keep the same `<line>` when a slot changes
 * count, so the existing bridge *slides* to its side position instead of being
 * removed and replaced — the swap used to read as a flicker under the pointer.
 */
export interface BridgeRailVm {
  id: string;
  /** Solid rather than dashed: a bridge that is on the board, not one on offer. */
  committed: boolean;
  /** Faded in. Both rails of a slot are always rendered, so this is a transition. */
  visible: boolean;
  /** Dimmed: committed, but the next click takes this slot back to zero. */
  removing: boolean;
  /** Sideways offset from the centred span. */
  transform: string;
}

/** A bridge slot, with its geometry resolved once and its state refreshed per change. */
export interface BridgeVm {
  sk: string;
  key: BridgeKey;
  /** Invisible wide hit area, so the whole span between islands is clickable. */
  hit: LineEndpoints;
  /** Centred span. Every rail is drawn on it and offset by its transform. */
  base: LineEndpoints;
  /** Offsets for the one- and two-bridge layouts. */
  railCenter: string;
  railMinus: string;
  railPlus: string;
  /** The lines to draw for this slot, committed and previewed alike. */
  rails: BridgeRailVm[];
}

/** Stable per-slot rail identities, so `trackBy` can keep the DOM nodes. */
const RAIL_IDS = ['rail-a', 'rail-b'];

/** An island, with its geometry resolved once and its state refreshed per change. */
/** Arrow keys map to a grid direction; the board is strictly orthogonal. */
const KEY_DIRECTIONS: Record<string, [number, number]> = {
  ArrowRight: [1, 0],
  ArrowLeft: [-1, 0],
  ArrowDown: [0, 1],
  ArrowUp: [0, -1],
};

/**
 * What the player is told about an island, after the settings have had their say.
 *
 * - `violation` — a rule is broken and the board alone proves it: too many
 *   bridges, no route left to reach the number, or a closed group that can never
 *   rejoin the rest. Drawn red with a cross.
 * - `deviates` — the count is right but at least one bridge is not the one the
 *   solution uses. Needs the solution, so it is drawn red *without* a cross:
 *   nothing here breaks a rule.
 * - `confirmed` — count right and every bridge matches the solution. Because the
 *   solution is unique, this island is finished for good.
 * - `satisfied` — the count is right; whether the bridges are is not being
 *   checked.
 */
export type BridgesIslandMark = 'none' | 'satisfied' | 'confirmed' | 'deviates' | 'violation';

export interface BridgesIslandVm {
  id: string;
  island: BridgesIsland;
  cx: number;
  cy: number;
  label: number;
  ariaLabel: string;
  satisfied: boolean;
  overpopulated: boolean;
  active: boolean;
  checkError: boolean;
  mark: BridgesIslandMark;
  ringRadius: number;
  tapRadius: number;
  crossA: LineEndpoints;
  crossB: LineEndpoints;
}

@Component({
  selector: 'lib-app-bridges-board',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './bridges-board.component.html',
  styleUrls: ['./bridges-board.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BridgesBoardComponent implements OnInit, OnDestroy {
  islands: BridgesIsland[] = [];
  bridges: BridgesMap = {};
  bridgeKeys: BridgeKey[] = [];

  cellSize = 60;
  islandRadius = 18;
  bridgeStroke = 6;
  bridgeOffset = 7;
  hitStrokeWidth = 28;
  islandFontSize = 16;
  islandTapExtra = 8;
  boardPadding = 28;

  widthPX = 600;
  heightPX = 400;

  /** Marks completed islands and the rule violations the board alone can prove. */
  highlightRightAndWrong = false;
  /** Adds the solution comparison on top. Implies everything the milder setting shows. */
  showErrorsImmediately = false;

  /** Undefined when the puzzle file ships no solution — the legacy node form. */
  private solution: BridgesMap | undefined;

  /**
   * Precomputed render model. The template reads these fields directly instead
   * of calling methods inside bindings — previously every x1/y1/x2/y2 of every
   * line invoked a geometry helper that rebuilt an island index from scratch,
   * which came to roughly 2000 calls per change-detection pass on a 61-island
   * board, and change detection runs on every pointer move.
   */
  bridgeVms: BridgeVm[] = [];
  islandVms: BridgesIslandVm[] = [];

  /** Text for the polite live region, so screen readers hear what a key did. */
  announcement = '';
  boardLabel = '';

  private readonly pointer = new BridgesPointerController();
  private puzzleID = '';
  private subs: Subscription[] = [];
  private checkFlash = false;
  private checkErrorNodeIds = new Set<string>();
  private checkTimeout: ReturnType<typeof setTimeout> | undefined;
  private touchHoverTimeout: ReturnType<typeof setTimeout> | undefined;
  private touchHoverArmedAt: number | null = null;
  private cachedSvg: SVGSVGElement | null = null;
  private originX = 0;
  private originY = 0;
  private resizeTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor(
    public framework: PuzzleFrameworkService,
    private store: Store,
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private winDialogService: PuzzleWinDialogService,
    private helpDialogService: PuzzleHelpDialogService,
    // Injecting settings modal service ensures its constructor runs and hooks SettingsMenu
    private _settingsModalService: PuzzleSettingsModalService
  ) {
    this.subs.push(
      this.winDialogService.winDialogRegistered.subscribe(() => {
        this.winDialogService.injectComponent(BridgesWinScreenComponent, {});
        this.winDialogService.showButtons([
          PUZZLE_WIN_DIALOG_BUTTONS.RESTART_GAME,
          PUZZLE_WIN_DIALOG_BUTTONS.BACK_TO_GAME,
        ]);
      })
    );
    this.subs.push(
      this.helpDialogService.helpDialogRegistered.subscribe(() => {
        this.helpDialogService.injectHelpComponent(BridgesHelpComponent, { translationKey: 'BRIDGES' });
      })
    );
  }

  ngOnInit(): void {
    this.applySizing();

    const parsed = parseBridgesPuzzleConfig(this.framework.puzzleConfig as BridgesPuzzleConfig);
    this.islands = parsed.islands;
    this.puzzleID = parsed.puzzleID;
    this.solution = parsed.solution;

    this.recomputeBoardDimensions();
    this.bridgeKeys = computeNeighborPairs(this.islands);
    this.bridges = this.makeEmptyBridges();

    this.store.dispatch(new SetPuzzleID({ puzzleID: this.puzzleID }));
    this.framework.initPuzzleGameState(this.puzzleID, PuzzleGameTypesEnum.BridgesGame, 0, undefined, undefined);
    this.hydrateOrInitBridgesState();
    this.boardLabel = this.translate.instant('BRIDGES.a11y.board_label', { count: this.islands.length });
    this.rebuildGeometry();

    this.subs.push(
      this.framework.buttonPressed.subscribe((button) => {
        if (button === Button.Check) this.onCheck();
        else if (button === Button.Reset) this.resetPuzzle();
      })
    );

    this.subs.push(
      this.store
        .select((state) => state.puzzleGameState.showErrorsImmediately)
        .subscribe((enabled: boolean) => {
          this.showErrorsImmediately = enabled;
          this.refresh();
        })
    );

    this.subs.push(
      this.store
        .select((state) => state.puzzleGameState.highlightRightAndWrongEnabled)
        .subscribe((enabled: boolean) => {
          this.highlightRightAndWrong = enabled;
          this.refresh();
        })
    );
  }

  ngOnDestroy(): void {
    for (const s of this.subs) s.unsubscribe();
    if (this.checkTimeout) clearTimeout(this.checkTimeout);
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    this.clearTouchHoverTimer();
  }

  // --- Template helpers ---

  /** Dash pattern derived from the stroke so previews read as dashed at every breakpoint. */
  get previewDash(): string {
    return `${(this.bridgeStroke * 0.9).toFixed(1)} ${(this.bridgeStroke * 1.8).toFixed(1)}`;
  }

  trackBridge(_index: number, vm: BridgeVm): string {
    return vm.sk;
  }

  trackRail(_index: number, rail: BridgeRailVm): string {
    return rail.id;
  }

  trackIsland(_index: number, vm: BridgesIslandVm): string {
    return vm.id;
  }

  islandCenter(n: BridgesIsland): { x: number; y: number } {
    return {
      x: this.boardPadding + (n.x - this.originX) * this.cellSize,
      y: this.boardPadding + (n.y - this.originY) * this.cellSize,
    };
  }

  // --- Pointer events ---

  mouseDownIsland(n: BridgesIsland): void {
    this.pointer.onMouseDownIsland(n);
  }
  mouseUpIsland(n: BridgesIsland): void {
    this.applyIntent(this.pointer.onMouseUpIsland(n));
  }
  cycleBridge(key: BridgeKey): void {
    this.applyIntent(this.pointer.onCycleBridgeRequested(key));
    // The pointer is still over this bridge, so re-arm hover: otherwise the
    // preview vanishes after a click and only returns once you leave and
    // re-enter the slot.
    this.pointer.onEnterBridge(key);
    this.refresh();
  }
  enterBridge(key: BridgeKey): void {
    this.pointer.onEnterBridge(key);
    this.refresh();
  }
  leaveBridge(): void {
    this.pointer.onLeaveBridge();
    this.refresh();
  }
  enterIsland(n: BridgesIsland): void {
    this.pointer.onEnterIsland(n);
    this.refresh();
  }
  leaveIsland(): void {
    this.pointer.onLeaveIsland();
    this.refresh();
  }

  /**
   * Keyboard play. Each island is a tab stop; an arrow key cycles the bridge
   * towards the neighbour in that direction, which is the same action clicking
   * that bridge performs. Without this the board was completely unplayable
   * without a pointer — there were no focusable elements at all.
   */
  onIslandKeydown(ev: KeyboardEvent, vm: BridgesIslandVm): void {
    const direction = KEY_DIRECTIONS[ev.key];
    if (!direction) return;
    ev.preventDefault();

    const target = raycast(this.islands, vm.island, direction[0], direction[1]);
    if (!target) {
      this.announce('BRIDGES.a11y.announce_none');
      return;
    }

    const key = { a: vm.island.id, b: target.id };
    const sk = serializeKey(key);
    const before = this.bridges[sk] ?? 0;
    this.tryCycleBridge(key);
    const after = this.bridges[sk] ?? 0;

    if (before === after) {
      this.announce('BRIDGES.a11y.announce_blocked');
    } else {
      this.announce('BRIDGES.a11y.announce_bridges', { count: after, required: target.requiredBridges });
    }
    this.refresh();
  }

  touchStartIsland(ev: TouchEvent, n: BridgesIsland): void {
    if (ev.touches && ev.touches.length > 1) return;
    const t = ev.touches?.[0];
    this.pointer.onTouchStartIsland(n, t ? this.clientToSvg(t.clientX, t.clientY) : null);
    this.preventDefault(ev);
    this.syncTouchHoverTimer();
    this.refresh();
  }

  /**
   * Repaints when a resting finger earns the dashed options.
   *
   * The controller only stores when the hold comes due; nothing else in the
   * component ticks, so without this the reveal would wait for the next pointer
   * event — which for a finger held perfectly still never arrives.
   */
  private syncTouchHoverTimer(): void {
    const due = this.pointer.pendingTouchHoverAt();
    if (due === null) {
      this.clearTouchHoverTimer();
      return;
    }
    if (this.touchHoverArmedAt === due) return;
    this.clearTouchHoverTimer();
    this.touchHoverArmedAt = due;
    this.touchHoverTimeout = setTimeout(() => {
      this.touchHoverTimeout = undefined;
      this.touchHoverArmedAt = null;
      if (this.pointer.settleTouchHover()) this.refresh();
    }, Math.max(0, due - Date.now()));
  }

  private clearTouchHoverTimer(): void {
    if (this.touchHoverTimeout) clearTimeout(this.touchHoverTimeout);
    this.touchHoverTimeout = undefined;
    this.touchHoverArmedAt = null;
  }

  @HostListener('document:mousemove', ['$event'])
  onDocMouseMove(ev: MouseEvent): void {
    // Bail before touching the DOM when no drag is in flight. These listeners
    // are bound for the component's whole life, so without this guard every
    // pointer move anywhere in the app forced a layout read plus a full change
    // detection pass.
    if (!this.pointer.snapshot().draggingFrom) return;
    const svg = this.clientToSvg(ev.clientX, ev.clientY);
    this.applyIntent(this.pointer.onMouseMove(svg, this.pointerContext()));
  }

  @HostListener('document:mouseup', ['$event'])
  onDocMouseUp(ev: MouseEvent): void {
    if (!this.pointer.snapshot().draggingFrom) return;
    const svg = this.clientToSvg(ev.clientX, ev.clientY);
    this.applyIntent(this.pointer.onMouseUp(svg, this.pointerContext()));
  }

  @HostListener('document:touchmove', ['$event'])
  onDocTouchMove(ev: TouchEvent): void {
    if (!this.pointer.snapshot().draggingFrom) return;
    const t = ev.touches?.[0];
    if (!t) return;
    const multi = !!ev.touches && ev.touches.length > 1;
    this.preventDefault(ev);
    const svg = this.clientToSvg(t.clientX, t.clientY);
    this.applyIntent(this.pointer.onTouchMove(svg, this.pointerContext(), multi));
    this.syncTouchHoverTimer();
  }

  @HostListener('document:touchend', ['$event'])
  onDocTouchEnd(ev: TouchEvent): void {
    if (!this.pointer.snapshot().draggingFrom) return;
    const t = ev.changedTouches?.[0] ?? ev.touches?.[0];
    const svg = t ? this.clientToSvg(t.clientX, t.clientY) : null;
    this.applyIntent(this.pointer.onTouchEnd(svg, this.pointerContext()));
    this.syncTouchHoverTimer();
  }

  @HostListener('document:touchcancel', ['$event'])
  onDocTouchCancel(_ev: TouchEvent): void {
    this.pointer.onTouchCancel();
    this.clearTouchHoverTimer();
    this.refresh();
  }

  /**
   * Sizing is breakpoint-based, so rotating a phone or resizing the window has
   * to rebuild both the metrics and the resolved geometry. Without this the
   * board kept its initial cell size and hit-target widths for the rest of the
   * session.
   */
  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  onViewportChange(): void {
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      const previousCellSize = this.cellSize;
      this.applySizing();
      this.cachedSvg = null; // its box changed; drop the cached element
      if (this.cellSize === previousCellSize) {
        this.refresh();
        return;
      }
      this.recomputeBoardDimensions();
      this.rebuildGeometry();
    }, 120);
  }

  // --- Internals ---

  private hydrateOrInitBridgesState(): void {
    const stored = this.store.selectSnapshot(BridgesGameState.getByID(this.puzzleID)) as BridgesGameModel | null;
    if (stored && stored.bridges) {
      // Only accept keys that are legal pairs on *this* board, and clamp counts
      // to 0..2. Persisted state can outlive the layout it was saved against
      // (regenerated puzzle, edited config), and stale keys otherwise feed
      // currentDegree/computeProgress/isSolved — which can both fake a win and
      // leave the board permanently unwinnable.
      const empty = this.makeEmptyBridges();
      const clean: BridgesMap = { ...empty };
      for (const sk of Object.keys(stored.bridges)) {
        if (!(sk in empty)) continue;
        const raw = Math.trunc(Number(stored.bridges[sk]));
        clean[sk] = Number.isFinite(raw) ? Math.max(0, Math.min(2, raw)) : 0;
      }
      this.bridges = clean;
      this.updateProgress();
    } else {
      this.store.dispatch(new InitBridgesGame({ puzzleID: this.puzzleID, bridges: { ...this.bridges } }));
    }
  }

  private persistBridges(): void {
    this.store.dispatch(new PersistBridgesState({ puzzleID: this.puzzleID, bridges: { ...this.bridges } }));
  }

  private makeEmptyBridges(): BridgesMap {
    const out: BridgesMap = {};
    for (const k of this.bridgeKeys) out[serializeKey(k)] = 0;
    return out;
  }

  private applySizing(): void {
    const vw = typeof window !== 'undefined' ? window.innerWidth || document.documentElement.clientWidth || 0 : 0;
    const vh = typeof window !== 'undefined' ? window.innerHeight || document.documentElement.clientHeight || 0 : 0;
    const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio as number) || 1 : 1;
    this.assignSizing(computeBridgesSizing(vw, vh, dpr));
    this.pointer.setCellSize(this.cellSize);
  }

  private assignSizing(s: BridgesSizing): void {
    this.cellSize = s.cellSize;
    this.islandRadius = s.islandRadius;
    this.bridgeStroke = s.bridgeStroke;
    this.bridgeOffset = s.bridgeOffset;
    this.hitStrokeWidth = s.hitStrokeWidth;
    this.islandFontSize = s.islandFontSize;
    this.islandTapExtra = s.islandTapExtra;
    this.boardPadding = s.boardPadding;
  }

  /**
   * Sizes the viewBox to the island bounding box.
   *
   * The old version added a whole extra cellSize while island centres started
   * at boardPadding, leaving ~10px of slack on the left/top and ~70px on the
   * right/bottom. Since the SVG is centred by preserveAspectRatio, that pushed
   * the visible grid noticeably up and to the left. Honouring minX/minY also
   * removes the dead space when a generated grid has an empty leading row.
   */
  private recomputeBoardDimensions(): void {
    const { minX, minY, maxX, maxY } = islandBounds(this.islands);
    this.originX = minX;
    this.originY = minY;
    this.widthPX = (maxX - minX) * this.cellSize + this.boardPadding * 2;
    this.heightPX = (maxY - minY) * this.cellSize + this.boardPadding * 2;
  }

  /** Resolves the geometry that only depends on layout + sizing, then the state. */
  private rebuildGeometry(): void {
    const offset = this.bridgeOffset;

    this.bridgeVms = this.bridgeKeys
      .map((key) => {
        const orientation = getOrientation(this.islands, key);
        if (!orientation) return null;
        const base =
          orientation === 'h'
            ? lineEndpointsH(key, this.islands, this.cellSize, this.boardPadding, this.originX, this.originY)
            : lineEndpointsV(key, this.islands, this.cellSize, this.boardPadding, this.originX, this.originY);
        return {
          sk: serializeKey(key),
          key,
          hit: base,
          base,
          railCenter: railTransform(orientation, 0),
          railMinus: railTransform(orientation, -offset),
          railPlus: railTransform(orientation, offset),
          rails: [] as BridgeRailVm[],
        };
      })
      .filter((vm): vm is BridgeVm => vm !== null);

    this.islandVms = this.islands.map((island) => {
      const c = this.islandCenter(island);
      const arm = this.islandRadius * 0.9;
      return {
        id: island.id,
        island,
        cx: c.x,
        cy: c.y,
        label: island.requiredBridges,
        ariaLabel: '',
        satisfied: false,
        overpopulated: false,
        active: false,
        checkError: false,
        mark: 'none',
        ringRadius: this.islandRadius + 6,
        tapRadius: this.islandRadius + this.islandTapExtra,
        crossA: { x1: c.x - arm, y1: c.y - arm, x2: c.x + arm, y2: c.y + arm },
        crossB: { x1: c.x - arm, y1: c.y + arm, x2: c.x + arm, y2: c.y - arm },
      };
    });

    this.refresh();
  }

  /** Recomputes only the per-change state on the existing geometry. */
  private refresh(): void {
    const snap = this.pointer.snapshot();
    const suppressHover = this.pointer.isSuppressingHover();
    const activeBridge = snap.dragPreviewKey ?? snap.hoveredBridgeKey;
    const activeIsland =
      snap.touchHoverIslandId ?? (snap.hoveredIslandId && !suppressHover ? snap.hoveredIslandId : null);

    for (const vm of this.bridgeVms) {
      const count = this.bridges[vm.sk] ?? 0;
      vm.rails = this.railsFor(vm, count, this.nextCountFor(vm, activeBridge, activeIsland));
    }

    // Only worth walking the whole board for these when something will actually
    // be drawn from them, and only the closed-group scan is board-wide.
    const anyMarking = this.highlightRightAndWrong || this.showErrorsImmediately;
    const stranded = anyMarking ? findClosedStrandedIslands(this.islands, this.bridges) : null;

    for (const vm of this.islandVms) {
      const degree = currentDegree(this.bridges, vm.island.id);
      vm.satisfied = degree === vm.island.requiredBridges;
      vm.overpopulated = degree > vm.island.requiredBridges;
      vm.active =
        snap.touchHoverIslandId === vm.id || (snap.hoveredIslandId === vm.id && !suppressHover);
      vm.checkError = this.checkFlash && this.checkErrorNodeIds.has(vm.id);
      vm.mark = this.markFor(vm, degree, stranded);
      vm.ariaLabel = this.islandAriaLabel(vm, degree);
    }

    this.cdr.markForCheck();
  }

  /**
   * Resolves an island to the single thing the player is shown.
   *
   * Rule violations outrank the solution comparison: a broken rule is both the
   * more urgent problem and the one the player can act on without being handed
   * an answer, so it must never be masked by a plain-red deviation mark.
   */
  private markFor(
    vm: BridgesIslandVm,
    degree: number,
    stranded: Set<string> | null
  ): BridgesIslandMark {
    if (!this.highlightRightAndWrong && !this.showErrorsImmediately) return 'none';

    if (
      vm.overpopulated ||
      stranded?.has(vm.id) ||
      isStarved(this.islands, this.bridgeKeys, this.bridges, vm.island)
    ) {
      return 'violation';
    }

    if (degree !== vm.island.requiredBridges) return 'none';

    // Below the required count a slot that is short of the solution is merely
    // unfinished, so the comparison only runs on a full island.
    if (this.showErrorsImmediately && this.solution) {
      return matchesSolution(this.islands, this.bridges, this.solution, vm.island)
        ? 'confirmed'
        : 'deviates';
    }
    return 'satisfied';
  }

  /** Colour alone carries the marking, so the state has to reach screen readers too. */
  private islandAriaLabel(vm: BridgesIslandVm, degree: number): string {
    const base = this.translate.instant('BRIDGES.a11y.island_label', {
      required: vm.island.requiredBridges,
      current: degree,
    });
    if (vm.mark === 'none' || vm.mark === 'satisfied') return base;
    return `${base}. ${this.translate.instant(`BRIDGES.a11y.island_state.${vm.mark}`)}`;
  }

  private announce(key: string, params?: Record<string, unknown>): void {
    // Re-assigning the same string would not re-trigger the live region, so
    // vary it with a zero-width space when the message repeats.
    const text = this.translate.instant(key, params);
    this.announcement = text === this.announcement ? `${text}​` : text;
  }

  /**
   * Builds the lines for one slot.
   *
   * Both rails always exist, whether or not anything is drawn on them: a line
   * that is created and destroyed can only pop in and out, and the dashed
   * options appearing under the pointer is exactly what read as a flash. As
   * standing elements they cross-fade instead.
   *
   * Committed bridges are never taken off the board to make room for a preview.
   * Going from one to two only *adds* a dashed line and slides the existing one
   * aside; going back to zero leaves both lines in place and dims them, so the
   * removal is announced rather than performed on hover.
   */
  private railsFor(vm: BridgeVm, count: number, next: number | null): BridgeRailVm[] {
    const removing = next === 0 && count > 0;
    const shown = next === null || removing ? count : Math.max(count, next);
    const previous = vm.rails;

    return RAIL_IDS.map((id, i) => {
      const visible = i < shown;
      return {
        id,
        // A hidden rail keeps the look it last had, so it fades out as whatever
        // the player was shown rather than snapping to the other style first.
        committed: visible ? i < count : previous[i]?.committed ?? false,
        visible,
        removing: removing && visible,
        // The second rail never moves: it is only ever drawn in the two-bridge
        // layout, so it can fade in where it already sits.
        transform: i === 0 ? (shown >= 2 ? vm.railMinus : vm.railCenter) : vm.railPlus,
      };
    });
  }

  /**
   * What this slot would hold after the next click, or `null` when the pointer
   * is elsewhere or the click would be refused for crossing another bridge —
   * in both cases the slot is drawn exactly as it stands.
   */
  private nextCountFor(
    vm: BridgeVm,
    activeBridge: string | null,
    activeIsland: string | null
  ): number | null {
    if (activeBridge === vm.sk) return this.nextNonCrossingCount(vm.key, vm.sk);
    if (activeIsland) {
      const [a, b] = vm.sk.split('|');
      if (a === activeIsland || b === activeIsland) return this.nextNonCrossingCount(vm.key, vm.sk);
    }
    return null;
  }

  /**
   * Maps client (screen) coordinates into the SVG user-space the island centres
   * live in.
   *
   * Must go through the element's own screen CTM: the SVG declares
   * `preserveAspectRatio="xMidYMid meet"`, which is a single uniform scale plus
   * a centering translate. Scaling each axis independently by
   * widthPX/rect.width is only equivalent when the container's aspect ratio
   * matches the board's — otherwise the result is off by up to a full cell and
   * drags resolve to the wrong island. The CTM also accounts for CSS transforms
   * and browser zoom, which manual arithmetic does not.
   */
  private clientToSvg(clientX: number, clientY: number): { x: number; y: number } {
    const svg = this.svgEl();
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return { x: clientX, y: clientY };
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    const mapped = point.matrixTransform(ctm.inverse());
    return { x: mapped.x, y: mapped.y };
  }

  private svgEl(): SVGSVGElement | null {
    if (!this.cachedSvg || !this.cachedSvg.isConnected) {
      this.cachedSvg = this.el.nativeElement.querySelector('svg');
    }
    return this.cachedSvg;
  }

  private pointerContext() {
    return {
      islands: this.islands,
      bridges: this.bridges,
      bridgeKeys: this.bridgeKeys,
      islandCenter: (n: BridgesIsland) => this.islandCenter(n),
    };
  }

  private applyIntent(intent: BridgesPointerIntent): void {
    if (intent.kind === 'commit') this.tryCycleBridge(intent.key);
    this.refresh();
  }

  /** `null` when the click would be refused, which is not the same as "goes to zero". */
  private nextNonCrossingCount(key: BridgeKey, sk: string): number | null {
    const current = this.bridges[sk] ?? 0;
    const next = (current + 1) % 3;
    if (next === 0) return 0;
    if (next > current && createsCrossing(this.islands, this.bridgeKeys, this.bridges, key, next)) return null;
    return next;
  }

  private tryCycleBridge(key: BridgeKey): void {
    const k = normalizeKey(this.islands, key);
    if (!k) return;
    if (!isVisiblePair(this.islands, k)) return;

    const sk = serializeKey(k);
    const current = this.bridges[sk] ?? 0;
    const next = (current + 1) % 3;
    if (next > current && createsCrossing(this.islands, this.bridgeKeys, this.bridges, k, next)) return;

    this.bridges = { ...this.bridges, [sk]: next };
    this.store.dispatch(new IncMoveCounter({ puzzleID: this.puzzleID }));
    this.persistBridges();
    this.updateProgress();
    if (isSolved(this.islands, this.bridges)) {
      this.store.dispatch(new SetFinished({ puzzleID: this.puzzleID }));
    }
  }

  private updateProgress(): void {
    this.store.dispatch(
      new SetGameProgress({ puzzleID: this.puzzleID, progress: computeProgress(this.islands, this.bridges) })
    );
  }

  private resetPuzzle(): void {
    this.bridges = this.makeEmptyBridges();
    this.framework.initPuzzleGameState(this.puzzleID, PuzzleGameTypesEnum.BridgesGame, 0);
    this.persistBridges();
    this.store.dispatch(new SetGameProgress({ puzzleID: this.puzzleID, progress: 0 }));
    this.refresh();
  }

  private onCheck(): void {
    if (isSolved(this.islands, this.bridges)) {
      this.store.dispatch(new SetFinished({ puzzleID: this.puzzleID }));
      return;
    }
    this.checkErrorNodeIds = findCheckErrors(this.islands, this.bridges);
    this.checkFlash = true;
    if (this.checkTimeout) clearTimeout(this.checkTimeout);
    this.checkTimeout = setTimeout(() => {
      this.checkFlash = false;
      this.refresh();
    }, 1000);
    this.refresh();
  }

  private preventDefault(ev: Event): void {
    try {
      ev.preventDefault();
    } catch {
      // ignore — passive listeners on some platforms
    }
  }
}

/**
 * A rail's sideways offset, as a CSS transform rather than shifted endpoints:
 * only a transform can be transitioned, and the slide is what keeps the
 * one-to-two change from reading as a flicker.
 */
function railTransform(orientation: 'h' | 'v', delta: number): string {
  return orientation === 'h' ? `translate(0px, ${delta}px)` : `translate(${delta}px, 0px)`;
}
